'use client';

import { useState } from 'react';
import { Link } from '@/i18n/routing';
import { useLocale, useTranslations } from 'next-intl';
import { JAZYKI_KONSPEKTA_INGDG, PUT_PROGRAMM_INGDG } from '@/lib/ingdg-konspekt-ssylki';
import { ThemeSwitcher } from '@/components/ui/theme-switcher';
import { Logo } from '@/components/ui/logo';
import { AccountMenu } from '@/components/layout/account-menu';
import { Menu, X } from 'lucide-react';

export function Header() {
  const t = useTranslations('layout');
  const tf = useTranslations('footer');
  const [open, setOpen] = useState(false);

  const locale = useLocale();

  /**
   * Меню витрины: только то, что интересно посетителю до входа.
   *
   * Пункта «Кабинет» здесь нет намеренно. Вошедший попадает туда через меню
   * профиля справа, а для гостя это ссылка в никуда: он всё равно окажется
   * на форме входа. Кабинет отделён от витрины, см.
   * docs/platform/ACCOUNT_AREA_UX.md §10.3.
   */
  const navLinks = [
    // Раздел программ (04.10.2026): Конспект и диктовка. Раньше здесь был
    // одиночный пункт «Konspekt» — владелец: нужен раздел, как на
    // aisearch.tech. Только на языках, где программы есть.
    ...(JAZYKI_KONSPEKTA_INGDG.includes(locale)
      ? [{ href: PUT_PROGRAMM_INGDG, label: t('software') }]
      : []),
    { href: '/solutions', label: t('solutions') },
    { href: '/features', label: tf('features') },
    { href: '/pricing', label: t('pricing') },
    { href: '/docs', label: t('docs') },
  ];

  return (
    <header className="sticky top-0 z-50 border-b border-[var(--color-border)] bg-[var(--color-bg)]/80 backdrop-blur-lg relative">
      <div className="max-w-[1280px] mx-auto px-6 h-16 flex items-center justify-between">
        {/* Hamburger + Logo */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => {
              setOpen(!open);
            }}
            className="md:hidden w-9 h-9 flex items-center justify-center rounded-lg border border-[var(--color-border)] hover:border-[var(--color-text-muted)] transition-colors"
            aria-label="Menu"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>

          <Link href="/" className="flex items-center gap-2.5 font-bold text-lg">
            <Logo className="w-7 h-7 text-[var(--color-brand-500)]" />
            <span className="tracking-tight">INGDG</span>
          </Link>
        </div>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-6 text-sm">
          {navLinks.map((l) => (
            <Link
              key={l.href}
              href={l.href}
              className="text-[var(--color-text-muted)] hover:text-[var(--color-accent-400)] transition-colors"
            >
              {l.label}
            </Link>
          ))}
        </nav>

        {/* Right */}
        <div className="flex items-center gap-3">
          <ThemeSwitcher />
          <AccountMenu />
        </div>
      </div>

      {/* Mobile Drawer — overlay, does not shift content */}
      {open && (
        <div className="md:hidden absolute top-full left-0 right-0 z-40 border-t border-[var(--color-border)] bg-[var(--color-bg)] shadow-2xl">
          <nav className="flex flex-col px-6 py-4 gap-2">
            {navLinks.map((l) => (
              <Link
                key={l.href}
                href={l.href}
                onClick={() => {
                  setOpen(false);
                }}
                className="px-3 py-2 rounded-lg hover:bg-[var(--color-bg-elevated)] text-[var(--color-text)] transition-colors"
              >
                {l.label}
              </Link>
            ))}
            {/* Вход и выход на узком экране живут в том же выдвижном меню:
                искать их в двух разных местах человеку не нужно. */}
            <div className="mt-2 pt-2 border-t border-[var(--color-border)]">
              <AccountMenu />
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
